/** The sort engine every `ResponsiveTable` caller shares. A plain `.ts` because vitest runs in node with no jsdom; `sortableHeader.tsx` holds the rendering half.
 *  Keyed on a per-row VALUE RESOLVER, not a hard-coded column union, so adding a table never widens a shared `SortKey`; each caller keeps its own key union as a type parameter. */

export type SortDir = 'asc' | 'desc'
export type SortState<K extends string> = { key: K; dir: SortDir } | null

/** Click cycle for a column header: ascending, descending, then off. "Off" is a real state: the unsorted order is the API's own and there is otherwise no way back to it short of reloading. */
export function nextSort<K extends string>(current: SortState<K>, key: K): SortState<K> {
  if (current?.key !== key) return { key, dir: 'asc' }
  if (current.dir === 'asc') return { key, dir: 'desc' }
  return null
}

/** What a column's value resolver may return. `null` (or `''`) sorts last in BOTH directions -- see `compare`. */
export type SortValue = string | number | null

/** Sorts a COPY of `rows`, returning the same reference when nothing is sorted. Reorders DISPLAY only, so edits keyed by row id are unaffected. */
export function sortRows<T, K extends string>(
  rows: T[],
  sort: SortState<K>,
  valueOf: (row: T, key: K) => SortValue,
): T[] {
  if (!sort) return rows
  const dir = sort.dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => compare(valueOf(a, sort.key), valueOf(b, sort.key), dir))
}

// Two numbers compare numerically; anything else as text. Blanks sort last in BOTH directions, so descending is not a strict reversal of ascending.
// A pair with one blank side always takes this branch, which keeps blanks last whatever the column's type.
function compare(a: SortValue, b: SortValue, dir: number): number {
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir
  const as = a == null ? '' : String(a)
  const bs = b == null ? '' : String(b)
  if (!as && !bs) return 0
  if (!as) return 1
  if (!bs) return -1
  return as.localeCompare(bs) * dir
}
